import { computed, type ComputedRef } from 'vue';
import { useSubscriptionStore } from '../stores/subscription';

/**
 * Banner types that can be displayed
 */
export type BannerType =
  | 'none'
  | 'trial-active'
  | 'trial-expiring'
  | 'trial-expired'
  | 'subscription-expiring'
  | 'subscription-canceled-grace'
  | 'subscription-expired'
  | 'convert-to-subscription'
  | 'free-tier'
  | 'free-tier-former-subscriber';

/**
 * Banner configuration returned by the service
 */
export interface BannerConfig {
  type: BannerType;
  message: string;
  buttonText: string;
  action: 'upgrade' | 'renew' | 'convert' | 'none';
  icon: string;
  variant: 'info' | 'warning' | 'trial' | 'success';
  daysRemaining?: number | null;
}

/**
 * Centralized subscription banner logic
 * Determines which banner to show based on subscription state
 */
export function useSubscriptionBanner(): {
  bannerConfig: ComputedRef<BannerConfig>;
  shouldShowBanner: ComputedRef<boolean>;
} {
  const subscriptionStore = useSubscriptionStore();

  /**
   * Determine which banner should be displayed based on subscription state
   * Priority order (highest to lowest):
   * 1. Canceled subscription in grace period (still has access)
   * 2. Active trial
   * 3. Subscription expiring soon
   * 4. Convert 6-month to subscription prompt
   * 5. Trial expired
   * 6. Subscription fully expired (former subscriber)
   * 7. Free tier (never subscribed)
   */
  const bannerConfig = computed<BannerConfig>(() => {
    // Don't show anything while loading
    if (subscriptionStore.loading) {
      return {
        type: 'none',
        message: '',
        buttonText: '',
        action: 'none',
        icon: '',
        variant: 'info',
      };
    }

    const subscription = subscriptionStore.subscription;
    const trial = subscriptionStore.trial;
    const conversions = subscriptionStore.conversions;
    const daysUntilExpiry = subscriptionStore.daysUntilExpiry;

    // Priority 1: Canceled subscription in grace period
    // User canceled but still has access until period end
    // Note: Stripe keeps status as 'active' with cancelAtPeriodEnd=true until the period actually ends
    if (subscription.cancelAtPeriodEnd &&
        subscription.currentPeriodEnd &&
        subscription.currentPeriodEnd > Date.now()) {

      const days = daysUntilExpiry;
      const dayText = days !== 1 ? 'days' : 'day';

      let message = '';
      if (days !== null && days <= 1) {
        message = `Your subscription has been cancelled and will expire ${days === 0 ? 'today' : 'tomorrow'}. Renew now to keep access to all your playlists and songs.`;
      } else {
        message = `Your subscription has been cancelled and will expire in ${days} ${dayText}. Renew now to keep access to all your playlists and songs.`;
      }

      return {
        type: 'subscription-canceled-grace',
        message,
        buttonText: 'Reactivate Subscription',
        action: 'renew',
        icon: '⚠️',
        variant: 'warning',
        daysRemaining: days,
      };
    }

    // Priority 2: Active trial (not expired)
    if (trial.isInTrial && trial.daysRemaining !== null && trial.daysRemaining >= 0) {
      const days = trial.daysRemaining;
      let message = '';

      if (days === 0) {
        message = 'Your trial expires today! Upgrade now to avoid losing access to unlimited features.';
      } else if (days <= 3) {
        message = `Trial expires in ${days} day${days !== 1 ? 's' : ''}! Upgrade now to continue enjoying unlimited playlists and songs.`;
      } else {
        message = `Trial expires in ${days} day${days !== 1 ? 's' : ''}! Upgrade now to continue enjoying unlimited playlists and songs.`;
      }

      return {
        type: days <= 3 ? 'trial-expiring' : 'trial-active',
        message,
        buttonText: 'Upgrade Now',
        action: 'upgrade',
        icon: '🎯',
        variant: 'trial',
        daysRemaining: days,
      };
    }

    // Priority 3: Active subscription expiring soon
    if (subscriptionStore.hasActiveSubscription && subscriptionStore.expiringSoon) {
      const days = daysUntilExpiry;
      const dayText = days !== 1 ? 'days' : 'day';

      return {
        type: 'subscription-expiring',
        message: `Your subscription expires in ${days} ${dayText}. Renew now to avoid service interruption.`,
        buttonText: 'Renew Subscription',
        action: 'renew',
        icon: '⚠️',
        variant: 'warning',
        daysRemaining: days,
      };
    }

    // Priority 4: Convert 6-month one-time to subscription
    if (conversions.showConvertPrompt && conversions.promptMessage) {
      return {
        type: 'convert-to-subscription',
        message: conversions.promptMessage,
        buttonText: 'Convert to Subscription',
        action: 'convert',
        icon: '🎯',
        variant: 'trial',
        daysRemaining: conversions.daysUntilExpiry,
      };
    }

    // Priority 5: Trial expired (but user was in trial)
    if (trial.daysRemaining !== null && trial.daysRemaining < 0 && !subscriptionStore.hasActiveSubscription) {
      return {
        type: 'trial-expired',
        message: 'Your free trial has expired. Upgrade to unlock unlimited playlists and songs!',
        buttonText: 'Upgrade Now',
        action: 'upgrade',
        icon: '🎯',
        variant: 'trial',
        daysRemaining: trial.daysRemaining,
      };
    }

    // Priority 6: Former subscriber (had paid subscription, now expired/canceled)
    const hadPaidSubscription = subscription.planType &&
                                 subscription.planType.startsWith('price_') &&
                                 (subscription.status === 'canceled' || subscription.status === 'expired');

    if (hadPaidSubscription && !subscriptionStore.hasActiveSubscription) {
      return {
        type: 'free-tier-former-subscriber',
        message: 'Your subscription has expired. Purchase premium access to see all your playlists and songs again!',
        buttonText: 'Renew Access',
        action: 'upgrade',
        icon: 'ℹ️',
        variant: 'info',
      };
    }

    // Priority 7: Free tier (never had subscription or only had trial)
    if (!subscriptionStore.hasActiveSubscription) {
      return {
        type: 'free-tier',
        message: 'You are on the free plan (1 playlist, 3 songs max). Upgrade to unlock unlimited playlists and songs!',
        buttonText: 'Upgrade Now',
        action: 'upgrade',
        icon: 'ℹ️',
        variant: 'info',
      };
    }

    // Default: No banner
    return {
      type: 'none',
      message: '',
      buttonText: '',
      action: 'none',
      icon: '',
      variant: 'info',
    };
  });

  const shouldShowBanner = computed(() => bannerConfig.value.type !== 'none');

  return {
    bannerConfig,
    shouldShowBanner,
  };
}
